import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import {
  IGenerateShareLinkResponse,
  IContractShareLinkItem
} from 'src/app/shared/models/v1/contract-vault/contract-vault.model';
import { ContractVaultHttpService } from 'src/app/shared/services/v1/contract-vault/contract-vault.http.service';

export interface IShareLinkDialogData {
  artifactId: number;
  fileName: string;
  category?: string;
}

export interface IDurationOption {
  label: string;
  minutes: number | null;
  icon: string;
  badge?: string;
}

/**
 * Modern, secure Share Link Configuration & Revocation Dialog Component.
 * Supports:
 * 1. Explicit user-triggered generation of presigned links with custom note & duration.
 * 2. Managing active shared links list with granular 1-click link revocation via ConfirmModal.
 * 3. Bulk revocation of all active share links in 1 click via ConfirmModal.
 */
@Component({
  selector: 'app-share-link-dialog',
  templateUrl: './share-link-dialog.component.html',
  styleUrls: ['./share-link-dialog.component.scss']
})
export class ShareLinkDialogComponent implements OnInit {
  public selectedTabIndex: number = 0;
  public selectedDuration: number | null = 1440; // Default: 24 Hours
  public accessType: 'view' | 'download' = 'view';
  public linkLabel: string = '';
  
  // Generation Output State
  public generatedLink: string = '';
  public expiresAt: string | null = null;
  public isPermanent: boolean = false;
  public isGenerating: boolean = false;
  public isCopied: boolean = false;
  public hasGenerated: boolean = false;
  public errorMessage: string | null = null;

  // Management State
  public existingLinks: IContractShareLinkItem[] = [];
  public isLoadingLinks: boolean = false;
  public isRevokingAll: boolean = false;
  public revokingIds = new Set<number>();
  public copiedLinkId: number | null = null;

  public readonly durationPresets: IDurationOption[] = [
    { label: '15 Minutes', minutes: 15, icon: 'bolt', badge: 'Quick' },
    { label: '1 Hour', minutes: 60, icon: 'schedule' },
    { label: '24 Hours', minutes: 1440, icon: 'today', badge: 'Popular' },
    { label: '7 Days', minutes: 10080, icon: 'date_range' },
    { label: '30 Days', minutes: 43200, icon: 'calendar_month' },
    { label: '1 Year', minutes: 525600, icon: 'history_edu' },
    { label: 'Permanent', minutes: null, icon: 'all_inclusive', badge: 'No Limit' }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IShareLinkDialogData,
    private dialogRef: MatDialogRef<ShareLinkDialogComponent>,
    private dialog: MatDialog,
    private contractVaultHttp: ContractVaultHttpService
  ) {}

  ngOnInit(): void {
    if (this.data?.artifactId) {
      this.loadExistingLinks();
    }
  }

  /**
   * Generates a new presigned share link ONLY when explicitly triggered by the user.
   */
  public generateLink(): void {
    if (!this.data?.artifactId || this.isGenerating) return;

    this.isGenerating = true;
    this.errorMessage = null;
    this.isCopied = false;

    this.contractVaultHttp.generateShareLink(
      this.data.artifactId,
      this.selectedDuration,
      this.linkLabel.trim() || undefined,
      this.accessType
    ).subscribe({
      next: (res) => {
        this.isGenerating = false;
        if (res && res.result) {
          this.generatedLink = res.result.shareUrl;
          this.expiresAt = res.result.expiresAt || null;
          this.isPermanent = res.result.isPermanent;
          this.hasGenerated = true;
          this.loadExistingLinks();
        } else {
          this.errorMessage = res?.message || 'Failed to generate share link.';
        }
      },
      error: (err) => {
        this.isGenerating = false;
        this.errorMessage = err?.error?.message || 'Failed to communicate with server.';
      }
    });
  }

  /**
   * Resets form to configure and generate another link.
   */
  public resetToCreateAnother(): void {
    this.hasGenerated = false;
    this.generatedLink = '';
    this.linkLabel = '';
    this.errorMessage = null;
    this.isCopied = false;
  }

  /**
   * Selects duration preset without triggering automatic creation.
   */
  public selectDuration(minutes: number | null): void {
    this.selectedDuration = minutes;
    if (this.hasGenerated) {
      this.hasGenerated = false;
      this.generatedLink = '';
    }
  }

  /**
   * Sets access mode without triggering automatic creation.
   */
  public setAccessType(type: 'view' | 'download'): void {
    if (this.accessType !== type) {
      this.accessType = type;
      if (this.hasGenerated) {
        this.hasGenerated = false;
        this.generatedLink = '';
      }
    }
  }

  /**
   * Loads all previously generated share links for this artifact.
   */
  public loadExistingLinks(): void {
    if (!this.data?.artifactId) return;

    this.isLoadingLinks = true;
    this.contractVaultHttp.getShareLinks(this.data.artifactId).subscribe({
      next: (res) => {
        this.isLoadingLinks = false;
        if (res && res.result) {
          this.existingLinks = res.result;
        }
      },
      error: () => {
        this.isLoadingLinks = false;
      }
    });
  }

  /**
   * Prompts user with a styled confirmation modal before revoking an individual link.
   */
  public revokeSingleLink(link: IContractShareLinkItem): void {
    if (!link?.id || this.revokingIds.has(link.id)) return;

    const labelText = link.label ? `"${link.label}"` : 'this direct link';
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        message: `Are you sure you want to revoke access for <strong>${labelText}</strong>?<br><br><span style="color: #dc2626; font-size: 12px;">Recipients will immediately lose access to view or download this document.</span>`,
        yesTitle: 'Revoke Access',
        yesColor: 'warn',
        falseTitle: 'Cancel',
        zIndex: 1100
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.performRevokeSingle(link);
      }
    });
  }

  private performRevokeSingle(link: IContractShareLinkItem): void {
    this.revokingIds.add(link.id);
    this.contractVaultHttp.revokeShareLink(link.id).subscribe({
      next: (res) => {
        this.revokingIds.delete(link.id);
        if (res && res.result) {
          link.isRevoked = true;
          link.revokedAt = new Date().toISOString();
        }
      },
      error: () => {
        this.revokingIds.delete(link.id);
      }
    });
  }

  /**
   * Prompts user with a styled confirmation modal before revoking ALL active share links.
   */
  public revokeAllActiveLinks(): void {
    if (!this.data?.artifactId || this.isRevokingAll) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        message: 'Are you sure you want to revoke <strong>ALL</strong> active shared links for this document?<br><br><span style="color: #dc2626; font-size: 12px;">Anyone with existing shared links will immediately lose access (HTTP 403 Forbidden).</span>',
        yesTitle: 'Revoke All Links',
        yesColor: 'warn',
        falseTitle: 'Keep Links',
        zIndex: 1100
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.performRevokeAll();
      }
    });
  }

  private performRevokeAll(): void {
    this.isRevokingAll = true;
    this.contractVaultHttp.revokeAllShareLinks(this.data.artifactId).subscribe({
      next: (res) => {
        this.isRevokingAll = false;
        if (res && res.result) {
          this.existingLinks.forEach(l => {
            if (!l.isRevoked) {
              l.isRevoked = true;
              l.revokedAt = new Date().toISOString();
            }
          });
        }
      },
      error: () => {
        this.isRevokingAll = false;
      }
    });
  }

  /**
   * Copies the newly generated link to the system clipboard.
   */
  public copyToClipboard(): void {
    if (!this.generatedLink) return;
    this.copyTextToClipboard(this.generatedLink);
    this.isCopied = true;
    setTimeout(() => {
      this.isCopied = false;
    }, 3000);
  }

  /**
   * Copies an existing link from the list.
   */
  public copyExistingLink(link: IContractShareLinkItem): void {
    if (!link?.shareUrl) return;
    this.copyTextToClipboard(link.shareUrl);
    this.copiedLinkId = link.id;
    setTimeout(() => {
      if (this.copiedLinkId === link.id) {
        this.copiedLinkId = null;
      }
    }, 3000);
  }

  private copyTextToClipboard(text: string): void {
    navigator.clipboard.writeText(text).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    });
  }

  /**
   * Opens the generated share link in a new browser tab for testing.
   */
  public openLink(): void {
    if (this.generatedLink) {
      window.open(this.generatedLink, '_blank');
    }
  }

  /**
   * Opens an existing share link in a new tab.
   */
  public openExistingLink(link: IContractShareLinkItem): void {
    if (link?.shareUrl) {
      window.open(link.shareUrl, '_blank');
    }
  }

  /**
   * Helper to count active links.
   */
  public get activeLinksCount(): number {
    return this.existingLinks.filter(l => !l.isRevoked && !l.isExpired).length;
  }

  /**
   * Closes the modal.
   */
  public close(): void {
    this.dialogRef.close();
  }
}
